import { createContext } from 'react';
import { ListRowType, ListColumnType, QueryParamType, ListMetaDataType, ListTooltipType } from 'types/ListTypes';
import { BulkSelectionState } from 'types/BulkActionTypes';

/*
 * ListContext
 * This context is created to contain information about lists which 
 * can be shared across the list components.
 */

interface ListContextProps<T extends ListRowType> {
    // Keep track if the list is loading
    loading: string;

    // Keep track of the query params of the list
    queryParams: QueryParamType;

    // Keep track of the data in the list
    data: T[];
    setData: (data: T[]) => void;

    // Keep track of the list meta data such as current page, total items and total pages
    listMetaData: ListMetaDataType;
    setListMetaData: (metaData: ListMetaDataType) => void;

    // Keep track of the extra meta data
    extraMeta: Record<string, any> | null;

    // Keep track of the ordering of the list
    ordering: string | null;

    // Keep track of the column whose sorting was cleared back to the default sorting
    clearedOrderingColumn: string | null;

    // Keep track of the table width class
    tableWidthClass: string;

    // Keep track of a search term is given
    searchTerm: string;
    setSearchTerm: (searchTerm: string) => void;

    // Keep track of the last search term
    lastSearchTerm: string | null;
    setLastSearchTerm: (lastSearchTerm: string | null) => void;

    // Keep track of the available columns in the list
    availableColumns: ListColumnType[];
    setAvailableColumns: (availableColumns: ListColumnType[]) => void; 

    // Keep track of the selected columns in the list
    selectedColumns: ListColumnType[];
    setSelectedColumns: (selectedColumns: ListColumnType[]) => void;

    // The columns as rendered, with fixed pixel widths in the full width layout
    displayColumns: ListColumnType[];

    // The minimum pixel width of the table
    tableMinWidth: number;

    // Keep track of the general list tooltip position
    listTooltip: ListTooltipType | null;
    setListTooltip: (listTooltip: ListTooltipType | null) => void;

    // Callback to update the query params (which refetches with the new params)
    updateQueryParams: (newParams: QueryParamType) => void;

    // Callback to handle the page change
    handlePageChange: (newPage: number) => void;

    // Callback to handle the search change
    handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;

    // Callback to handle the column ordering
    handleColumnOrdering: (columnField: string) => void;

    // Callback to handle the 
    handleDragOrdering: (newOrder: ListRowType[]) => void;

    // Callback to refetch the list
    refetchList: () => void;

    // Keep track of the bulk selection state, include or exclude mode with ids
    selection: BulkSelectionState;
    setSelection: (selection: BulkSelectionState) => void;

    // Callback to toggle the selection of a single row
    toggleRowSelection: (id: number) => void;

    // Callback to toggle the header select all checkbox
    toggleSelectAll: () => void;

    // Keep track of the derived number of currently selected rows
    selectedCount: number;

    // The translation key of the object name, used for the bulk actions panel
    objectName?: string;

    // Whether the user chosen columns feature is active for this list
    hasCustomListColumns?: boolean;

    // Callback to open the column selection modal
    openColumnsModal?: () => void;

    // Callback to persist a new column width after a header resize
    handleColumnResizeEnd?: (field: string, width: number) => void;

    // Callback to persist the new column order after a header drag
    handleColumnReorderEnd?: (field: string, targetIndex: number) => void;
}

const ListContext = createContext<ListContextProps<any>>(undefined!);

export default ListContext;